import config from "../../config";
import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import classNames from "classnames";
import { History } from "history";
import {
  IconPhoneCall,
  IconDownload,
  IconAlertSquareRounded,
  IconSquareRoundedCheck,
  IconArchive,
  IconForbid2,
} from "@tabler/icons-react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import JSZip from "jszip";

import { FormattedMessage, useIntl } from "react-intl";

import { Dropdown, Tooltip, OverlayTrigger } from "react-bootstrap";
import {
  IonPage,
  IonContent,
  IonToggle,
  useIonToast,
  IonSpinner,
  IonList,
  IonLabel,
  IonItem,
  IonModal,
  IonButtons,
  IonButton,
  IonHeader,
  IonToolbar,
} from "@ionic/react";
import EmptyConversationSvg from "../../theme/images/no-conversation-selected.svg";
import SparksSvg from "../../theme/images/sparks.svg";

// Validator
import { validateMessage } from "../../utils/validators";

import { Contact, ChatInputRef, Message } from "../../interfaces";

//API
import ContactsApi from "../../api/contacts";
import AiApi from "../../api/ai";
import LeadsApi from "../../api/leads";

// Components
import ChatList from "../ChatList";
import ChatDetail from "../chat-detail";
import ContactDetails from "../ContactDetails";
import UploadPhotoModal from "../UploadPhotoModal";
import CustomToggle from "../CustomToggle";
import ChatInput from "../common/chat-input";
import FileInput from "../common/file-input";
import ContactPlatform from "../common/contact-platform";
import ContactFilters, {
  SearchContacts,
  ContactsBuckets,
} from "../common/contact-filters";
import Call from "../common/call";
import EscalationAlert from "../common/escalation";
import Kanban from "../kanban";

import useContactsStore from "../../store/contacts";
import { useSocketStore } from "../../store/socket";
import { useAppStore } from "../../store/app";
import { useHandleNewMessage, useSelectedContactData } from "../../hooks";
import { getChatId } from "../../utils/serialize";

import styles from "./styles.module.css";
import tableStyles from "./table.module.css";
import "./Chats.css";

const NewConversation = lazy(() => import("../new-conversation"));

const isDev = config.ENV === "staging" || config.ENV === "local";

interface ILayoutProps {
  mode: "chats" | "leads" | "kanban";
  history: History;
}

const Chats: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const { history } = props;

  const settings = useAppStore((state) => state.settings);
  const { socketReady } = useSocketStore();

  const queryClient = useQueryClient();

  const [followUps, setFollowUps] = useState<{ active: boolean }>({
    active: false,
  });

  const updateContactsDb = useContactsStore((state) => state.updateContactsDb);
  const [show, setShow] = useState(false);
  const [body, setBody] = useState("");
  const [selectionNonce, setSelectionNonce] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  const [scrollEscalations, setScrollEscalations] = useState(0);
  const [infoOpen, toggleInfoOpen] = useState(true);
  const [isOpenUpload, setIsOpenUpload] = useState(false);
  const [isLoadingUpload, setIsLoadingUpload] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [isOpenDocsUpload, setIsDocsOpenUpload] = useState<boolean>(false);
  const [isOpenNewConv, setOpenNewConv] = useState<boolean>(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const hiddenFileInput = useRef<HTMLInputElement | null>(null);
  const hiddenDocInput = useRef<HTMLInputElement | null>(null);
  const chatInputRef = React.useRef<ChatInputRef>(null);

  const isFunctionActive = followUps?.active || false;

  const chatId = useMemo(
    () => getChatId(history.location.pathname, props.mode),
    [history.location.pathname],
  );

  const contactData = useSelectedContactData(chatId);
  const [present] = useIonToast();
  const intl = useIntl();

  const filters = useContactsStore((state) => state.filters);
  const isArchivedBucket = filters.find(
    (f) => f.label === "bucket" && f.value === "archived",
  )?.isActive;

  // Contact detail event handler
  const handleDetail = useCallback(
    (c: Contact) => {
      console.log("setting up contact detail");
      updateContactsDb([c]);
    },
    [updateContactsDb],
  );

  const { data: contactInfo, isLoading: isRequestingContact } = useQuery({
    queryFn: () => ContactsApi.getContact(chatId),
    queryKey: ["getContact", chatId, selectionNonce], // Forces refetch on selection change
    refetchOnWindowFocus: false,
    enabled: !!chatId,
  });

  useEffect(() => {
    if (chatId) {
      setShow(true);
    }
  }, [chatId]);

  const { data: aiSettings } = useQuery({
    queryFn: () => AiApi.getAiSettings(),
    queryKey: ["getAiSettings"],
    refetchOnWindowFocus: false,
  });

  const mutateArchive = useMutation({
    mutationFn: ContactsApi.putArchive,
    onSuccess: (data) => {
      if (!data.success) {
        return present({
          message: intl.formatMessage({ id: "common.tryItLater" }),
          duration: 6000,
          color: "light",
          position: "top",
          cssClass: "error-toast",
        });
      }
    },
    onError: () => {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 6000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    },
  });

  const handleToggleFollowUps = async (value: boolean) => {
    const { err } = await AiApi.putAiSettingsByField("followUps.active", value);
    if (err) {
      present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 3000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    }
    queryClient.invalidateQueries({ queryKey: ["getAiSettings"] });
  };

  const escapeCSVValue = (value: any): string => {
    const stringValue = String(value ?? ""); // Maneja null/undefined
    const escaped = stringValue.replace(/"/g, '""'); // Escapa comillas dobles
    return `"${escaped}"`; // Encierra todo entre comillas dobles
  };

  const download = (blob: Blob, filename = "data.csv") => {
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = async () => {
    setExportLoading(true);
    const { contacts, toolCalls } = await LeadsApi.getExport();
    setExportLoading(false);
    const zip = new JSZip();
    if (contacts?.length) {
      const rows = contacts.map((row: any[]) =>
        row.map(escapeCSVValue).join(","),
      );
      zip.file("leads.csv", rows.join("\n"));
    }
    if (toolCalls?.length) {
      const rows = toolCalls.map((row: any[]) =>
        row.map(escapeCSVValue).join(","),
      );
      const csvContent = rows.join("\n");
      zip.file("actions.csv", rows.join("\n"));
    }
    const blob = await zip.generateAsync({ type: "blob" });
    download(blob, "export-leads.zip");
  };

  useEffect(() => {
    if (aiSettings?.data?.aiSettings?.followUps) {
      setFollowUps(aiSettings?.data?.aiSettings?.followUps);
    }
  }, [aiSettings]);

  useEffect(() => {
    if (
      contactInfo &&
      contactInfo?._id &&
      !isRequestingContact &&
      settings.companies.length
    ) {
      const hasCompanyMatch = contactInfo?.companies?.some((obj2: any) =>
        settings.companies.some((obj1) => obj1.id === obj2._id),
      );
      if (!hasCompanyMatch) return history.replace("/chats");
      handleDetail(contactInfo);
    }
  }, [contactInfo, isRequestingContact, settings.companies]);
  useHandleNewMessage();
  useEffect(() => {
    focusChatInput();
    setBody("");
    setReplyTo(null);
  }, [contactData.id]);

  useEffect(() => {
    if (contactData.messages.length) {
      const lastMessage = contactData.messages[contactData.messages.length - 1];
      if (lastMessage.id) {
        ContactsApi.readMessages(contactData.contact.id, lastMessage.id);
      }
    }
  }, [contactData.messages.length]);

  const sendMessage = async (content: string) => {
    setBody("");
    setReplyTo(null);
    const { err } = await ContactsApi.postMessages(contactData.id, {
      content,
      replyTo: replyTo?.id,
    });
    if (err) {
      if (err.error) {
        return present({
          message: err.error?.[intl.locale],
          duration: 6000,
          color: "warning",
          position: "bottom",
        });
      }
      console.log(err);
    }
  };

  const sendPhotos = async (content: FormData) => {
    setIsLoadingUpload(true);
    const { err } = await ContactsApi.uploadMedia(contactData.id, content);
    setIsLoadingUpload(false);
    if (err) {
      if (err.error) {
        return present({
          message: err.error?.[intl.locale],
          duration: 6000,
          color: "warning",
          position: "bottom",
        });
      }
    }
    setIsOpenUpload(false);
    return onHandleModalClose();
  };

  const selectChat = useCallback(
    async (contactId: string) => {
      setSelectionNonce((n) => n + 1);
      history.replace(`/${props.mode}/${contactId}${history.location.search}`);
      toggleInfoOpen(true);
    },
    [history, toggleInfoOpen],
  );

  const enableOrDisableContact = async (enabled: boolean) => {
    const { err } = await ContactsApi.putEnabled(contactData.id, { enabled });
    if (err) {
      console.log(err);
    }
  };

  const solveEscalations = async (id: string) => {
    const { err } = await ContactsApi.putSolveEscalations(id);
    if (err) {
      console.log(err);
    }
  };

  const handleSendSubmit = async () => {
    const { err } = validateMessage(body);
    if (err) {
      return;
    }
    return sendMessage(body);
  };

  const handleInputChange = (body: string) => setBody(body);

  const handleAutoChange = (event: CustomEvent<{ checked: boolean }>) => {
    return enableOrDisableContact(event.detail.checked);
  };

  const toggleBlock = async (contact: Contact) => {
    const { id, blocked } = contact;

    const { err } = await ContactsApi.putBlocked(id, { blocked: !blocked });
    if (err) {
      console.log(err);
    }
  };

  const runConversational = async () => {
    try {
      const { err } = await ContactsApi.runConversational(contactData.id);
      if (err) {
        console.log(err);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleCloseOffCanvas = () => {
    setShow(false);
    //resetContact();
    const base = props.mode;
    const url = `/${base}${history.location.search}`;
    history.replace(url);
  };

  const openSendPhoto = () => {
    hiddenFileInput.current?.click();
  };

  const openSendDoc = () => {
    hiddenDocInput.current?.click();
  };

  const scrollToEscalation = (messageId?: string) => {
    if (messageId) {
      const messageEl = document.getElementById(messageId);
      if (messageEl) {
        return messageEl.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }
    }
    return setScrollEscalations(scrollEscalations + 1);
  };

  const onHandleModalClose = () => {
    setIsDocsOpenUpload(() => false);
    setIsOpenUpload(() => false);

    if (hiddenFileInput?.current?.value) {
      hiddenFileInput.current.value = "";
    }
    if (hiddenDocInput.current?.value) {
      hiddenDocInput.current.value = "";
    }

    setFiles([]);
    return setDocFiles([]);
  };

  const onChangeFileInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "photo" | "doc",
  ) => {
    setIsLoadingUpload(false);
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      const files = [...fileList];

      if (type === "photo") {
        setFiles(files);
        return setIsOpenUpload(true);
      }

      setDocFiles(files);
      return setIsDocsOpenUpload(true);
    }
  };

  const createEscalation = async (contactId: string) => {
    await ContactsApi.escalateContact(contactId, {});
  };

  const focusChatInput = () => chatInputRef.current?.setInputFocus();

  const TableHeader = (
    <IonList style={{ margin: 0, padding: 0 }}>
      <IonItem className={tableStyles.listItemHeader} lines="full">
        <IonLabel>
          <div
            className={`d-flex justify-content-between align-items-center ${tableStyles.listItemHolder}`}
          >
            <div className="d-flex align-items-center gap-2 fs-2 color-step-800 fw-semibold">
              <div className={`${tableStyles.date}`}>
                <FormattedMessage id="leads.date" />
              </div>

              {settings.multiAccount ? (
                <div className={tableStyles.account}>
                  <FormattedMessage id="common.company" />
                </div>
              ) : null}
              <div className={tableStyles.platform}></div>
              <div className={`${tableStyles.name} fs-2 color-step-900`}>
                <FormattedMessage id="common.name" />
              </div>

              <div className={tableStyles.score}>
                <FormattedMessage id="leads.score" />
              </div>
              <div className={tableStyles.action}>
                <FormattedMessage id="leads.intent" />
              </div>
              <div className={tableStyles.action}>
                <FormattedMessage id="leads.followUp" />
              </div>
              <div className={tableStyles.actions}>
                <FormattedMessage id="leads.filter.actions" />
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 justify-content-end"></div>
          </div>
        </IonLabel>
      </IonItem>
    </IonList>
  );
  const ToggleFollowUps = (
    <div className="flex gap-3 align-items-center">
      <div className="flex align-items-center justify-content-between gap-2">
        <label className="mb-0" htmlFor="enable-help">
          <FormattedMessage
            id={
              isFunctionActive
                ? "ai.settings.followUps.enable"
                : "ai.settings.followUps.disabled"
            }
          />
        </label>
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={
            <Tooltip className="visito-tooltip">
              <FormattedMessage id="ai.settings.followUps.enable.desc" />
            </Tooltip>
          }
        >
          <IonToggle
            id="enable-help"
            name="enable-help"
            checked={isFunctionActive}
            onIonChange={(ev) => handleToggleFollowUps(ev.detail.checked)}
            mode="ios"
            className="ios visito-toggle"
          />
        </OverlayTrigger>
      </div>
      <div>
        <button
          disabled={exportLoading}
          className={`btn btn-visito-light btn-sm btn-flex`}
          onClick={() => handleExport()}
        >
          <IconDownload size={14} />
          <div>
            {exportLoading ? (
              <FormattedMessage id={`common.loading`} />
            ) : (
              <FormattedMessage id={`leads.export`} />
            )}
          </div>
        </button>
      </div>
    </div>
  );

  const ChatListHeader = (
    <div className="chat-list-header">
      <div className="flex justify-content-between align-items-center gap-2 px-3 pt-3 mb-0">
        <h1 className="fs-6 fw-semibold mb-0">
          <FormattedMessage
            id={props.mode === "chats" ? "nav.inbox" : "nav.leads"}
          />
        </h1>
        {settings.isWaReady && (
          <>
            <button
              className={`${styles.newConvBtn} fs-3 fw-semibold-1`}
              onClick={() => setOpenNewConv(true)}
            >
              <FormattedMessage id="common.newConversation" />
            </button>
            <Suspense fallback="Loading...">
              <NewConversation
                isOpen={isOpenNewConv}
                onOpenChange={setOpenNewConv}
                settings={settings}
              />
            </Suspense>
          </>
        )}
      </div>
      {!isArchivedBucket && (
        <div className="d-flex align-items-sm-center chat-list-header-search">
          <SearchContacts />
          <div className="d-flex align-items-center">
            <ContactFilters companies={settings.companies} defaultClass="" />
          </div>
        </div>
      )}

      <div className="chat-list-header-review">
        {!socketReady ? (
          <div className="connection-status fs-1 color-step-700 flex align-items-center ">
            <IonSpinner color="medium" name="lines-sharp-small"></IonSpinner>{" "}
            <FormattedMessage id="common.connecting" />
          </div>
        ) : (
          <div
            className={
              props.mode === "leads"
                ? `assignation-nav flex gap-1 align-items-center justify-content-between`
                : "assignation-nav"
            }
          >
            {!isArchivedBucket && (
              <ContactsBuckets fill={props.mode === "leads" ? false : true} />
            )}

            {props.mode === "leads" && (
              <div
                className="flex gap-3 align-items-center"
                style={{ paddingRight: "12px" }}
              >
                {ToggleFollowUps}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
  const ChatListComponent = (
    <div
      className={
        props.mode === "chats"
          ? "chat-list"
          : props.mode === "leads"
          ? "chat-table"
          : "kanban-table"
      }
    >
      {["leads", "chats"].includes(props.mode) ? ChatListHeader : null}
      {props.mode === "leads" ? TableHeader : null}

      {["leads", "chats"].includes(props.mode) ? (
        <ChatList
          mode={"desktop"}
          type={props.mode}
          select={selectChat}
          isMulti={settings.multiAccount}
          styles={tableStyles}
          selectedId={chatId}
        />
      ) : (
        <div className={`d-flex w-100 ${styles.kanbanContainer}`}>
          {["Lead", "Availability", "Payment", "Booked"].map((col) => (
            <div key={col} className={styles.kanbanColumn}>
              <div className={styles.kanbanColumnHeader}>
                <span className={styles.columnTitle}>{col}</span>
                <span className="notif-pill btn-primary rounded-pill fw-bold">
                  3
                </span>
              </div>
              <div className={styles.kanbanCards}>
                <ChatList
                  mode={"desktop"}
                  type={props.mode}
                  select={selectChat}
                  isMulti={settings.multiAccount}
                  styles={tableStyles}
                  selectedId={chatId}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const ChatPresentationComponent = contactData.id ? (
    <div className="chat-presentation">
      <div className="chat-interface">
        <div className="chat-interface-header">
          <div className="navigation">
            <div
              className="chat-header-contact-info"
              onClick={() => toggleInfoOpen(!infoOpen)}
            >
              <ContactPlatform
                defaultClass="user-platform"
                contact={contactData.contact}
              />
              {settings.multiAccount &&
                contactData.companies.map((c) => (
                  <div className="user-company mr-1" key={c.id}>
                    <span className="badge text-bg-light">{c.name}</span>
                  </div>
                ))}

              <div className="user-info">{contactData.contact.name}</div>
              {contactData.contact.escalated ? (
                <span className="badge text-bg-warning">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div className="fw-semibold-1">
                      <FormattedMessage id="esc.needsReview" />
                    </div>
                  </div>
                </span>
              ) : null}
            </div>
            <div className="contact-tools">
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 100, hide: 1000 }}
                overlay={
                  <Tooltip className="visito-tooltip">
                    <FormattedMessage id="chats.aiAuto" />
                  </Tooltip>
                }
              >
                <IonToggle
                  checked={contactData.contact.enabled}
                  onIonChange={handleAutoChange}
                  mode={"ios"}
                  style={{ width: "max-content" }}
                  className="visito-toggle-conversation ios"
                >
                  <img
                    src={SparksSvg}
                    width={23}
                    style={{ marginRight: "5px" }}
                    alt=""
                  ></img>
                </IonToggle>
              </OverlayTrigger>

              {settings.isVoiceReady && isDev ? (
                <div className="ml-1">
                  <Call
                    displayName={`${contactData.contact.name} ${contactData.contact.phoneNumber}`}
                    className="btn btn-visito-light btn-xs btn-flex"
                    icon={
                      <>
                        <IconPhoneCall size={17} />
                        <FormattedMessage id="aiSettingsCall" />
                      </>
                    }
                    onCall={() => console.log("calling")}
                    onHangUp={() => console.log("hangup")}
                    contactId={contactData.contact.id}
                    type={"outbound"}
                  />
                </div>
              ) : null}
              {
                <Dropdown style={{ marginLeft: "12px" }}>
                  <Dropdown.Toggle
                    id="dropdown-custom-components"
                    as={CustomToggle}
                    title="options"
                  >
                    Custom toggle
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() =>
                        contactData.contact.escalated
                          ? solveEscalations(contactData.contact.id)
                          : createEscalation(contactData.contact.id)
                      }
                      eventKey="1"
                      className="flex align-items-center gap-1"
                    >
                      {contactData.contact.escalated ? (
                        <IconSquareRoundedCheck size={17} />
                      ) : (
                        <IconAlertSquareRounded size={17} />
                      )}
                      <FormattedMessage
                        id={
                          contactData.contact.escalated
                            ? "chats.markAsClose"
                            : "chats.markAsOpen"
                        }
                      />
                    </Dropdown.Item>

                    <Dropdown.Item
                      onClick={() =>
                        mutateArchive.mutate({
                          contactId: contactData.id,
                          archive: !contactData.contact.archive,
                        })
                      }
                      eventKey="2"
                      className="flex align-items-center gap-1"
                    >
                      <IconArchive size={17} />
                      <FormattedMessage
                        id={
                          contactData.contact.archive
                            ? `chats.unArchive`
                            : `chats.archive`
                        }
                      />
                    </Dropdown.Item>

                    <Dropdown.Item
                      onClick={() => toggleBlock(contactData.contact)}
                      eventKey="3"
                      className="flex align-items-center gap-1"
                    >
                      <IconForbid2 size={17} />
                      <FormattedMessage
                        id={
                          contactData.contact.blocked
                            ? `chats.unblockContact`
                            : `chats.blockContact`
                        }
                      />
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              }
            </div>
          </div>
        </div>
        {contactData.contact.escalated && (
          <EscalationAlert
            escalation={contactData.escalations?.[0]}
            solveEscalations={() => solveEscalations(contactData.contact.id)}
            scrollToEscalation={scrollToEscalation}
          />
        )}
        <div className="chat-interface-body">
          <ChatDetail
            mode={"desktop"}
            scrollEscalations={scrollEscalations}
            replyTo={({ message }: { message: Message }) => setReplyTo(message)}
            actions={["reply", "flag", "clone", "debug"]}
            isLoading={isRequestingContact}
            selectedId={chatId}
          />
        </div>
        <div className="chat-interface-footer">
          <FileInput
            ref={hiddenFileInput}
            accept="image/png, image/jpeg, image/webp"
            multiple
            onChange={(e) => onChangeFileInput(e, "photo")}
            style={{ display: "none" }}
          />
          <FileInput
            ref={hiddenDocInput}
            accept="application/pdf, .pdf"
            onChange={(e) => onChangeFileInput(e, "doc")}
            style={{ display: "none" }}
          />
          <ChatInput
            ref={chatInputRef}
            id="send-body"
            value={body}
            placeholder={intl.formatMessage({
              id: contactData.contact.blocked
                ? "chats.blocked"
                : "chats.newMessageInput",
            })}
            channel={contactData.contact.type}
            onInputChange={handleInputChange}
            onOpenPhotoInput={openSendPhoto}
            onOpenDocInput={openSendDoc}
            onRunConversational={runConversational}
            onBeforeSubmit={handleSendSubmit}
            disabled={contactData.contact.blocked}
            replyTo={replyTo}
            cleanReplyTo={() => setReplyTo(null)}
          />
        </div>
      </div>
      <div
        className={classNames("chat-tools", {
          open: infoOpen === true,
        })}
      >
        <div className="chat-tools-header">
          <div
            onClick={() => toggleInfoOpen(false)}
            className="chat-tools-close"
          >
            <FormattedMessage id="contactDetail.header" />
          </div>
        </div>
        <div className="chat-tools-body">
          <ContactDetails
            toolCalls={contactData.toolCalls}
            bookings={contactData.bookings}
            contact={contactData.contact}
            createEscalation={createEscalation}
            solveEscalations={solveEscalations}
            toggleBlock={toggleBlock}
          />
        </div>
      </div>
    </div>
  ) : (
    <div className="chat-welcome">
      <div className="welcome-content text-center">
        <div className="row justify-content-center">
          <div className="mb-3">
            <img alt="" src={EmptyConversationSvg}></img>
          </div>
          <span className="color-step-900 font-14 fw-semibold-1">
            <FormattedMessage id="chats.emptyWelcomeTitle" />
          </span>
          <span className="color-step-600 font-12">
            <FormattedMessage id="chats.emptyWelcomeSubtitle" />
          </span>
        </div>
      </div>
    </div>
  );

  const TableOffCanvas = (
    <IonModal
      isOpen={show}
      onWillDismiss={handleCloseOffCanvas}
      className="lead-modal"
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="end">
            <IonButton
              className="color-visito-2"
              onClick={handleCloseOffCanvas}
            >
              <FormattedMessage id="common.close" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="page-wrapper">
          <div className="chat-wrapper-page">{ChatPresentationComponent}</div>
        </div>
      </IonContent>
    </IonModal>
  );

  return (
    <IonPage id="main-content">
      <IonContent className="ion-padding">
        <div className="page-wrapper">
          <div className="chat-wrapper-page">
            {ChatListComponent}
            {["leads", "kanban"].includes(props.mode)
              ? null
              : ChatPresentationComponent}
          </div>
        </div>
        {["leads", "kanban"].includes(props.mode) ? TableOffCanvas : null}
        <UploadPhotoModal
          show={isOpenUpload || isOpenDocsUpload}
          sending={isLoadingUpload}
          handleClose={onHandleModalClose}
          files={isOpenDocsUpload ? docFiles : files}
          upload={sendPhotos}
          mode={`desktop`}
          areDocs={isOpenDocsUpload}
          channel={contactData.contact.type}
        />
      </IonContent>
    </IonPage>
  );
};

export default Chats;
